import React from 'react';
import { FiCornerUpLeft } from 'react-icons/fi';

// Wrapper component pour l'icône de réponse (compatible TypeScript)
const ReplyIcon: React.FC<{ size?: number; style?: React.CSSProperties }> = ({ size = 16, style }) => {
  const IconComponent = FiCornerUpLeft as any;
  return <IconComponent size={size} style={style} />;
};

// Interface pour les couleurs de priorité
interface PriorityColors {
  normal: { bg: string; text: string };
  high: { bg: string; text: string };
  urgent: { bg: string; text: string };
}

// Interface pour le message
export interface MessageItemData {
  id: number;
  subject: string;
  content: string;
  sender_name: string;
  sender_type?: string | null;
  sent_date?: string;
  sent_time?: string;
  date?: string;
  created_date?: string;
  priority?: 'normal' | 'high' | 'urgent';
  student_id?: number | null;
  student_name?: string | null;
  student_gender?: 'Male' | 'Female' | null;
  student_label?: string | null;
  is_reply?: boolean;
  reply_to_message_id?: number | null;
  // Pour compatibilité avec TeacherDashboard
  from?: string;
}

// Props du composant MessageItem
export interface MessageItemProps {
  message: MessageItemData;
  isOpen: boolean;
  onToggle: (messageId: number, e?: React.MouseEvent) => void;
  
  // Styles
  useModernStyle?: boolean;
  windowWidth: number;
  
  // Fonctions utilitaires
  translateReportType: (subject: string) => string;
  translatePriority: (priority: string) => string;
  getPlainText: (content: string | null | undefined, maxLength: number) => string;
  getCleanedContent: (content: string | null | undefined) => string;
  
  // Affichage expéditeur/destinataire
  showSenderLabel: boolean; // true pour "من:", false pour "إلى:"
  senderLabel?: string; // "من" ou "إلى"
  getSenderTitle?: (senderType: string | null | undefined) => string;
  
  // Étudiant
  getStudentGender: (studentId: number | null | undefined) => 'Male' | 'Female' | null | undefined;
  getStudentLabel?: (gender: 'Male' | 'Female' | null | undefined) => string;
  
  // Date
  formatDate: (date: string) => string;
  formatDateTime?: (date: string, time?: string) => string; // Pour TeacherDashboard
  
  // Actions
  showReplyButton?: boolean;
  onReply?: (message: MessageItemData) => void;
  showReplyBadge?: boolean;
  
  // Traductions
  t: (key: string) => string;
  
  // Couleurs de priorité personnalisées (optionnel)
  priorityColors?: PriorityColors;
}

const MessageItem: React.FC<MessageItemProps> = ({
  message,
  isOpen,
  onToggle,
  useModernStyle = false,
  windowWidth,
  translateReportType,
  translatePriority,
  getPlainText,
  getCleanedContent,
  showSenderLabel,
  senderLabel,
  getSenderTitle,
  getStudentGender,
  getStudentLabel,
  formatDate,
  formatDateTime,
  showReplyButton = false,
  onReply,
  showReplyBadge = false,
  t,
  priorityColors
}) => {
  // Couleurs de priorité par défaut
  const defaultPriorityColors: PriorityColors = {
    normal: { bg: '#f3f4f6', text: '#6b7280' },
    high: { bg: '#fee2e2', text: '#dc2626' },
    urgent: { bg: '#fef3c7', text: '#d97706' }
  };
  
  const colors = priorityColors || defaultPriorityColors;
  const priority = (message.priority || 'normal').toLowerCase();
  const priorityColor = colors[priority as keyof typeof colors] || colors.normal;
  
  // Préparer les données
  const reportType = translateReportType(message.subject);
  const previewText = getPlainText(message.content, 80);
  
  // Formatage de la date
  const messageDate = message.sent_date || message.date || message.created_date || '';
  const messageTime = message.sent_time || '';
  let formattedDateDisplay: string;
  
  if (formatDateTime) {
    // TeacherDashboard utilise formatDateTime
    formattedDateDisplay = formatDateTime(messageDate, messageTime);
  } else {
    // ParentDashboard utilise formatDate + messageTime séparément
    formattedDateDisplay = formatDate(messageDate) + (messageTime ? ` ${messageTime}` : '');
  }
  
  // Obtenir le label de l'étudiant
  const getStudentLabelValue = (): string => {
    if (message.student_label) return message.student_label;
    if (message.student_gender && getStudentLabel) {
      return getStudentLabel(message.student_gender);
    }
    if (message.student_id) {
      const gender = getStudentGender(message.student_id);
      if (gender && getStudentLabel) {
        return getStudentLabel(gender);
      }
    }
    return t('studentLabel') !== 'studentLabel' ? t('studentLabel') : 'الطالب/ـة';
  };
  
  const studentLabel = getStudentLabelValue();
  
  // Construire le label de l'expéditeur/destinataire
  const getSenderDisplay = (): string => {
    const senderName = message.from || message.sender_name || t('notSpecified');
    
    if (showSenderLabel) {
      // Pour "من:" (messages reçus)
      if (getSenderTitle && message.sender_type) {
        const title = getSenderTitle(message.sender_type);
        if (title) {
          return `${senderLabel || t('from')} ${title}: ${senderName}`;
        }
      }
      return `${senderLabel || t('from')} ${senderName}`;
    } else {
      // Pour "إلى:" (messages envoyés)
      return `${senderLabel || t('to')}: ${senderName}`;
    }
  };
  
  // Styles selon useModernStyle
  const cardStyle: React.CSSProperties = useModernStyle
    ? {
        backgroundColor: isOpen ? '#FFF9E6' : '#FFFFFF',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '12px',
        border: isOpen ? '2px solid #FFE0B2' : '1px solid #E5E7EB',
        boxShadow: isOpen ? '0 2px 8px rgba(0, 0, 0, 0.08)' : '0 1px 3px rgba(0, 0, 0, 0.05)',
        transition: 'all 0.2s ease',
        cursor: 'pointer'
      }
    : {
        backgroundColor: isOpen ? '#FFF9E6' : '#FAFAFA',
        borderRadius: '8px',
        padding: '12px',
        marginBottom: '8px',
        border: isOpen ? '2px solid #FFE0B2' : '1px solid #E0E0E0',
        transition: 'all 0.2s ease',
        cursor: 'pointer'
      };
  
  const subjectFontSize = useModernStyle
    ? (windowWidth < 768 ? '14px' : '15px')
    : (windowWidth < 768 ? '13px' : '14px');
  
  const previewFontSize = useModernStyle
    ? (windowWidth < 768 ? '12px' : '14px')
    : (windowWidth < 768 ? '11px' : '12px');
  
  const metadataFontSize = useModernStyle
    ? (windowWidth < 768 ? '12px' : '13px')
    : (windowWidth < 768 ? '10px' : '11px');
  
  const chevronSize = useModernStyle ? '18px' : '16px';
  const chevronColor = useModernStyle ? '#9ca3af' : '#6b7280';
  
  // Gestionnaires d'événements
  const handleClick = (e: React.MouseEvent) => {
    // Ne pas déclencher si on clique sur le bouton de réponse
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    onToggle(message.id, e);
  };
  
  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    if (windowWidth >= 768) {
      if (useModernStyle) {
        e.currentTarget.style.backgroundColor = isOpen ? '#FFF9E6' : '#F9FAFB';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
      } else {
        e.currentTarget.style.backgroundColor = isOpen ? '#FFF9E6' : '#F5F5F5';
      }
    }
  };
  
  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    if (windowWidth >= 768) {
      if (useModernStyle) {
        e.currentTarget.style.backgroundColor = isOpen ? '#FFF9E6' : '#FFFFFF';
        e.currentTarget.style.boxShadow = isOpen ? '0 2px 8px rgba(0, 0, 0, 0.08)' : '0 1px 3px rgba(0, 0, 0, 0.05)';
      } else {
        e.currentTarget.style.backgroundColor = isOpen ? '#FFF9E6' : '#FAFAFA';
      }
    }
  };
  
  return (
    <div
      key={message.id}
      style={cardStyle}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Ligne compacte avec sujet et début du texte */}
      <div style={{
        display: 'flex',
        alignItems: useModernStyle ? 'flex-start' : 'center',
        gap: useModernStyle ? '16px' : '12px',
        width: '100%'
      }}>
        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: useModernStyle ? '10px' : '8px',
            marginBottom: useModernStyle ? '10px' : '6px',
            flexWrap: 'nowrap',
            overflow: 'hidden'
          }}>
            {/* Badge "رد" pour les messages envoyés qui sont des réponses */}
            {showReplyBadge && (message.is_reply || message.reply_to_message_id) && (
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: useModernStyle ? '4px 10px' : '2px 8px',
                borderRadius: '8px',
                fontSize: useModernStyle ? (windowWidth < 768 ? '10px' : '11px') : '10px',
                fontWeight: '600',
                backgroundColor: '#ede9fe',
                color: '#7c3aed',
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}>
                <ReplyIcon size={useModernStyle ? 12 : 10} />
                {t('replyBadge')}
              </span>
            )}
            
            <span style={{
              fontSize: subjectFontSize,
              fontWeight: '600',
              color: '#1f2937',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              fontFamily: useModernStyle ? 'inherit' : undefined,
              lineHeight: useModernStyle ? '1.5' : undefined
            }} dir="auto">
              {reportType || message.subject || t('noSubject')}
            </span>
            
            {!isOpen && (
              <span style={{
                fontSize: previewFontSize,
                color: '#6b7280',
                fontWeight: '400',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                fontFamily: useModernStyle ? 'inherit' : undefined
              }} dir="auto">
                - {previewText}
              </span>
            )}
          </div>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: useModernStyle ? '14px' : '12px',
            fontSize: metadataFontSize,
            color: '#6b7280',
            flexWrap: 'wrap',
            fontFamily: useModernStyle ? 'inherit' : undefined,
            marginTop: useModernStyle ? '4px' : undefined
          }}>
            <span style={{ whiteSpace: 'nowrap', fontSize: useModernStyle ? 'inherit' : undefined }} dir="auto">
              {getSenderDisplay()}
            </span>
            
            {message.student_name && (
              <span style={{
                whiteSpace: 'nowrap',
                color: '#059669',
                fontWeight: '500',
                fontSize: useModernStyle ? 'inherit' : undefined
              }} dir="auto">
                {studentLabel}: {message.student_name}
              </span>
            )}
            
            <span style={{ whiteSpace: 'nowrap', fontSize: useModernStyle ? 'inherit' : undefined }}>
              {formattedDateDisplay}
            </span>
            
            <div style={{
              backgroundColor: priorityColor.bg,
              color: priorityColor.text,
              padding: useModernStyle ? '4px 10px' : '2px 8px',
              borderRadius: '12px',
              fontSize: useModernStyle ? (windowWidth < 768 ? '11px' : '12px') : (windowWidth < 768 ? '10px' : undefined),
              fontWeight: '600',
              whiteSpace: 'nowrap',
              fontFamily: useModernStyle ? 'inherit' : undefined
            }}>
              {translatePriority(message.priority || 'normal')}
            </div>
          </div>
        </div>
        
        <span style={{
          fontSize: chevronSize,
          color: chevronColor,
          transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s ease',
          flexShrink: 0,
          marginTop: useModernStyle ? '2px' : undefined
        }}>
          ▶
        </span>
      </div>

      {/* Contenu complet quand ouvert */}
      {isOpen && (
        <div style={{
          marginTop: useModernStyle ? '20px' : '16px',
          paddingTop: useModernStyle ? '20px' : '16px',
          borderTop: useModernStyle ? '2px solid #FFE0B2' : '1px solid #FFE0B2'
        }}>
          <div style={{
            fontSize: useModernStyle ? (windowWidth < 768 ? '13px' : '14px') : (windowWidth < 768 ? '12px' : '13px'),
            color: '#374151',
            lineHeight: useModernStyle ? '1.7' : '1.6',
            marginBottom: useModernStyle ? '16px' : '12px',
            wordWrap: 'break-word',
            whiteSpace: 'pre-wrap',
            fontFamily: useModernStyle ? 'inherit' : undefined,
            ...(useModernStyle ? {
              padding: '12px',
              backgroundColor: '#FAFAFA',
              borderRadius: '8px',
              border: '1px solid #F3F4F6'
            } : {})
          }} dir="auto">
            {getCleanedContent(message.content)}
          </div>
          
          {/* Bouton de réponse */}
          {showReplyButton && onReply && (
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              paddingTop: useModernStyle ? '12px' : '8px',
              borderTop: '1px solid #FFE0B2'
            }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onReply(message);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: useModernStyle ? '8px' : '6px',
                  padding: useModernStyle ? '10px 20px' : '8px 16px',
                  backgroundColor: '#7c3aed',
                  color: 'white',
                  border: 'none',
                  borderRadius: useModernStyle ? '8px' : '6px',
                  cursor: 'pointer',
                  fontSize: useModernStyle ? (windowWidth < 768 ? '13px' : '14px') : '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease',
                  ...(useModernStyle ? {
                    boxShadow: '0 2px 4px rgba(124, 58, 237, 0.2)'
                  } : {})
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#6d28d9';
                  if (useModernStyle) {
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(124, 58, 237, 0.3)';
                  }
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '#7c3aed';
                  if (useModernStyle) {
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(124, 58, 237, 0.2)';
                  }
                }}
              >
                <ReplyIcon size={16} />
                <span>{t('replyButton')}</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MessageItem;

